import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../../components/AppToast";
import CommonDataTable from "../../../components/CommonDataTable";
import {
  defaultVisibleFields,
  getColumnWidth,
  getValueFormatter,
  getColumnLabel
} from "../constant/PartnerListColumns";
import { getallpartner } from "../../../services/partner/partner";
import { applyEntityLabel } from "./partnerEntityConfig";
import { flattenStorerRecord, STORER_ROW_ONLY_FIELDS } from "./storerForm";
import { usePermission } from "../../../hooks/usePermission";

function disableDetailRows(row, label) {
  if (!row) return [];
  const address = [row["address.partner_address1"], row["address.partner_address2"]]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(", ");
  const location = [
    row["address.partner_city"],
    row["address.partner_state"],
    row["address.partner_pincode"],
    row["address.partner_country"],
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(", ");

  return [
    ["Name", row.partner_name],
    [`${label} ID`, row.partner_id],
    ["Email", row.partner_email],
    ["Contact", row.partner_mobile],
    ["Address", address],
    ["Location", location],
    ["Status", row.status],
  ].filter(([, value]) => String(value ?? "").trim());
}

function DisableConfirmModal({ open, entityLabel, row, onClose, onConfirm }) {
  const label = entityLabel || "Customer";
  const lower = label.toLowerCase();
  const id = row?.partner_id;
  const details = disableDetailRows(row, label);

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      backdrop="static"
      dialogClassName="pp-disable-dialog"
      contentClassName="pp-disable-content"
    >
      <Modal.Body className="pp-disable-body">
        <button type="button" className="btn-close pp-disable-close" aria-label="Close" onClick={onClose} />
        <div className="d-flex align-items-start gap-3">
          <span className="pp-disable-mark" aria-hidden>
            <i className={`bi ${lower === "supplier" ? "bi-shop" : "bi-person-vcard"}`} />
          </span>
          <div>
            <div className="pp-disable-kicker">Confirm disable</div>
            <h2 className="pp-disable-title">Disable this {lower}?</h2>
            <p className="pp-disable-copy">
              {id
                ? `Are you sure you want to disable this ${lower} ID ${id}?`
                : `Are you sure you want to disable this ${lower}?`}
            </p>
          </div>
        </div>

        {details.length > 0 && (
          <dl className="pp-disable-details">
            {details.map(([field, detail]) => (
              <div className="pp-disable-detail" key={field}>
                <dt>{field}</dt>
                <dd className={field.endsWith("ID") ? "pp-disable-detail-id" : undefined}>
                  {field === "Status" ? (
                    <span className={`pp-disable-status pp-disable-status--${String(detail).toLowerCase()}`}>
                      {detail}
                    </span>
                  ) : (
                    detail
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </Modal.Body>
      <Modal.Footer className="pp-disable-footer">
        <button type="button" className="btn btn-outline-secondary px-4" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn pp-disable-confirm px-4"
          onClick={() => {
            onConfirm?.();
            onClose();
          }}
        >
          Disable
        </button>
      </Modal.Footer>
    </Modal>
  );
}

const startCase = (str) =>
  (str || "")
    .replace(/([A-Z]+)/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bAsn\b/gi, "ASN")
    .replace(/\bSku\b/gi, "SKU")
    .replace(/\bId\b/gi, "ID")
    .trim();

export default function PartnerList({
  onViewPartner,
  refreshKey,
  onEditPartner,
  showCreateButton = false,
  createButtonText = "Add New Partner",
  createButtonIcon = "bi-plus-lg",
  onCreateClick,
  /** Entity overrides — defaults keep Partner Management behaviour unchanged. */
  entityLabel = "Partner",
  fetchList = getallpartner,
  tableId = "partner-table",
  exportImportPath = "partner",
  sampleFileUrl = "/samples/partner.xlsx",
  sampleFileName = "partner",
  /** When set, the table pages this array and does not call the list API. */
  localRows = null,
  /** Used when the list API throws. Partner leaves this empty. */
  fallbackRows = null,
  /** Customer / Supplier: API prefix (`customer` | `supplier`) and the columns to show first. */
  fieldPrefix = "",
  visibleFields = null,
  onDisable,
  onOpenShip,
  shipActionLabel = "",
}) {

  const { hasPermission } = usePermission();

  const permissionModuleMap = {
    Customer: "customer-management",
    Supplier: "supplier-management",
    Partner: "partnermanagement",
  };
  // console.log("entity", entityLabel);


  const permissionModule =
    permissionModuleMap[entityLabel] || "partnermanagement";

  const canView = hasPermission(permissionModule, "VIEW");
  const canAdd = hasPermission(permissionModule, "ADD");
  const canEdit = hasPermission(permissionModule, "UPDATE");
  const canImport = hasPermission(permissionModule, "IMPORT");
  const canExport = hasPermission(permissionModule, "EXPORT");

  const [allPossibleColumns, setAllPossibleColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  /** ───────────────────────────────
   *  Load Partner List
   *  ─────────────────────────────── */
  const loadList = useCallback(async (pageNo, pageSizeNo) => {
    const requestedPage = typeof pageNo === "object" && pageNo
      ? Number(pageNo.page) || 0
      : Number(pageNo) || 0;
    const requestedSize = typeof pageNo === "object" && pageNo
      ? Number(pageNo.pageSize) || pageSize
      : Number(pageSizeNo) || pageSize;

    setLoading(true);
    try {

      let res;
      if (Array.isArray(localRows)) {
        res = {
          data: localRows.slice(requestedPage * requestedSize, (requestedPage + 1) * requestedSize),
          total: localRows.length,
        };
      } else {
        try {
          res = await fetchList(requestedPage, requestedSize);
          if (!res || !Array.isArray(res.data)) {
            throw new Error("Invalid list response");
          }
        } catch (apiError) {
          if (!Array.isArray(fallbackRows) || fallbackRows.length === 0) throw apiError;
          res = {
            data: fallbackRows.slice(requestedPage * requestedSize, (requestedPage + 1) * requestedSize),
            total: fallbackRows.length,
          };
        }
      }

      const total = res.total;

      const apiData = res.data;
      const fieldSet = new Set();
      const preferredVisible = Array.isArray(visibleFields) && visibleFields.length
        ? visibleFields
        : defaultVisibleFields;

      const builtRows = fieldPrefix
        ? (apiData || []).map((rec, idx) => {
          const flat = flattenStorerRecord(rec, fieldPrefix);
          if (!flat.id) flat.id = rec?._id || idx;
          Object.keys(flat).forEach((k) => {
            if (k !== "id" && k !== "_id" && !STORER_ROW_ONLY_FIELDS.includes(k)) fieldSet.add(k);
          });
          return flat;
        })
        : (apiData || []).map((rec, idx) => {


          const bank = rec.bank || {};
          const address = rec.address || {};
          const tenant = rec.tenantDetails || {};
          const partnerType = rec.partnerTypeDetails || {};
          const modules = rec.moduleDetails || [];
          const facilities = rec.facilityDetails || [];
          const userDetails1 = rec.userDetails || {};
          const operationalHours = (rec.operationalHours || [])
            .map(
              (d) =>
                `${d.dayRange}: ${d.timeRange?.open || ""}–${d.timeRange?.close || ""}`
            )
            .join(", ");

          const flat = {
            id: rec._id || idx,
            _id: rec._id,
            partner_id: rec.partner_id || "",
            partner_type: rec.partner_type || "",
            acceptTerms: rec.acceptTerms || "",
            documents: rec.documents || [],
            partner_name: rec.partner_name || "",
            partner_email: rec.partner_email || "",
            partner_mobile: rec.partner_mobile || "",
            partner_noOfUserLicenses:
              rec.partner_noOfUserLicenses !== undefined
                ? String(rec.partner_noOfUserLicenses)
                : "",
            "partnerTypeDetails.value": partnerType.value || "",
            "tenantDetails.tenant_name": tenant.tenant_name || "",
            "bank.bankName": bank.bankName || "",
            "bank.ifscCode": bank.ifscCode || "",
            "bank.accountNumber": bank.accountNumber || "",
            "address.partner_address1": address.partner_address1 || "",
            "address.partner_address2": address.partner_address2 || "",
            "address.partner_city": address.partner_city || "",
            "address.partner_state": address.partner_state || "",
            "address.partner_pincode": address.partner_pincode || "",
            "address.partner_country": address.partner_country || "",
            "moduleDetails.value": modules.map((m) => m.value).join(", "),
            "moduleDetails.code": modules.map((m) => m._id).join(", "),
            "facilityDetails.value": facilities.map((m) => m.Facility_Name).join(", "),
            "facilityDetails.code": facilities.map((m) => m._id).join(", "),
            operationalHours,
            tenantId: rec.tenantId || "",
            status: rec.status === 1 ? "Active" : rec.status === 2 ? "Pending" : "Inactive",
            createdBy: rec.createdBy || "",
            updatedBy: rec.updatedBy || "",
            createdAt: rec.createdAt
              ? new Date(rec.createdAt).toLocaleString()
              : "",
            updatedAt: rec.updatedAt
              ? new Date(rec.updatedAt).toLocaleString()
              : "",
            userDetails: userDetails1
          };

          Object.keys(flat).forEach((k) => {
            if (k !== "id" && k !== "__raw") fieldSet.add(k);
          });

          return flat;
        });

      if (!fieldPrefix) {
        defaultVisibleFields.forEach((f) => fieldSet.add(f));
      }
      preferredVisible.forEach((f) => fieldSet.add(f));

      // Sort remaining fields alphabetically
      const remaining = Array.from(fieldSet)
        .filter((f) => !preferredVisible.includes(f))
        .sort((a, b) => a.localeCompare(b));

      const orderedFields = [...preferredVisible.filter((f) => fieldSet.has(f)), ...remaining];

      // Actions column
      const actionsColumn = {
        field: "__actions",
        headerName: "",
        width: 72,
        minWidth: 72,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            size="small"
            aria-label={`${entityLabel} actions`}
            onClick={(e) => handleOpenMenu(e, params.row)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )
      };

      const cols = [
        actionsColumn,
        ...orderedFields.map((f) => ({
          field: f,
          headerName: applyEntityLabel(getColumnLabel(f, startCase), entityLabel),
          width: getColumnWidth(f),
          valueFormatter: getValueFormatter(f)
        }))
      ];

      setAllPossibleColumns(cols);
      setRows(builtRows);
      setTotalCount(total);
    } catch (error) {
      toast.error(
        `Failed to load ${entityLabel} data: ${error?.message || error || "Unknown error"}`
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchList, entityLabel, localRows, fallbackRows, pageSize, fieldPrefix, visibleFields]);

  /** ───────────────────────────────
   *  Effects
   *  ─────────────────────────────── */
  useEffect(() => {
    loadList(page, pageSize);
  }, [loadList, refreshKey, page, pageSize]);

  /** ───────────────────────────────
   *  Context Menu Handlers
   *  ─────────────────────────────── */
  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleView = () => {
    if (menuRow && onViewPartner) {
      onViewPartner(menuRow);
    }
    handleCloseMenu();
  };

  const handleEdit = () => {
    if (menuRow && onEditPartner) {
      onEditPartner(menuRow);
    }
    handleCloseMenu();
  };

  const handleOpenShip = () => {
    if (menuRow && onOpenShip) onOpenShip(menuRow);
    handleCloseMenu();
  };

  const handleDelete = () => {
    if (menuRow) setConfirmDelete(menuRow);
    handleCloseMenu();
  };

  /** ───────────────────────────────
   *  Render
   *  ─────────────────────────────── */
  return (
    <>
      {loading && <div className="text-center py-3">Loading {entityLabel} data...</div>}

      <CommonDataTable
        id={tableId}
        allPossibleColumns={allPossibleColumns}
        initialVisible={[
          "__actions",
          ...(Array.isArray(visibleFields) && visibleFields.length ? visibleFields : defaultVisibleFields),
        ]}
        initialRows={rows}
        height={500}
        rowsPerPageOptions={[10, 25, 50, 100]}
        // onDataRefresh={loadList}
        checkboxSelection
        exportImportPath={exportImportPath}
        sampleFileUrl={sampleFileUrl}
        sampleFileName={sampleFileName}
        showCreateButton={canAdd}
        createButtonText={createButtonText}
        createButtonIcon={createButtonIcon}
        onCreateClick={onCreateClick}
        enableImportButton={canImport}
        enableExportButton = {canExport}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}

        onManualRefresh={loadList}
        forInventory={true}
        onDataRefresh={async ({ filters = [], page: p = 0, pageSize: ps = pageSize } = {}) => {
          // NOTE: this just forwards the values to your fetchHandler. For now we console.log as requested.
          // console.log("ASN ....................... received onDataRefresh:", { filters, page: p, pageSize: ps });
          // call your existing fetchHandler (we'll update it to accept filters)
          // await loadList(p, ps, filters);
        }}
      />


      {/* Context Menu */}
      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {canView && (
          <MenuItem onClick={handleView}>
            <i className="bi bi-eye me-2"></i> View
          </MenuItem>
        )}

        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <i className="bi bi-pencil me-2"></i> Edit
          </MenuItem>
        )}
        {shipActionLabel && onOpenShip && (
          <MenuItem onClick={handleOpenShip}>
            <i className="bi bi-geo-alt me-2"></i> {shipActionLabel}
          </MenuItem>
        )}
        {onDisable && (
          <MenuItem onClick={handleDelete}>
            <i className="bi bi-slash-circle me-2"></i> Disable
          </MenuItem>
        )}
      </Menu>

      <DisableConfirmModal
        open={!!confirmDelete}
        entityLabel={entityLabel}
        row={confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => onDisable?.(confirmDelete)}
      />
    </>
  );
}
